import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import api from "../../services/api";
import CircularLoader from "../UI/Loader";
import { GlobalContext } from "../../contexts/globalContext";
import { FaArrowDown, FaMapMarkerAlt } from "react-icons/fa";

export default function PopularRides() {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setSearchFields } = useContext(GlobalContext);

  useEffect(() => {
    showAllRides();
  }, []);

  const showAllRides = async () => {
    try {
      setLoading(true);
      const params = { limit: 6, offset: 0 };
      const response = await api.get(`ride/popular`, { params });
      setRides(response.data.data);
      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const handleSearchRide = (ride) => {
    setSearchFields({
      goingTo: ride.heading_to,
      leavingFrom: ride.leaving_from,
    });
    navigate(
      `/ride-sharing?leavingFrom=${ride.leaving_from}&goingTo=${ride.heading_to}`
    );
  };

  const renderRoute = (ride) => {
    const from = ride.leaving_from === ride.heading_to ? ride.pickup_location.substring(0, 20) : ride.leaving_from;
    const to = ride.leaving_from === ride.heading_to ? ride.drop_location.substring(0, 20) : ride.heading_to;

    return (
      <RouteInfo>
        <Location>
            <FaMapMarkerAlt className="icon" /> {from}
        </Location>
        <ArrowWrapper><FaArrowDown /></ArrowWrapper>
        <Location>
             <FaMapMarkerAlt className="icon" /> {to}
        </Location>
      </RouteInfo>
    );
  };

  if(!loading && rides.length === 0) return null;

  return (
    <SectionContainer>
      <HeaderWrapper>
        <SectionTitle>Popular Routes</SectionTitle>
        <ViewAllLink to="/carpool">View All Rides →</ViewAllLink>
      </HeaderWrapper>

      <GridContainer>
        {loading && <LoaderWrapper><CircularLoader isLoading={loading} /></LoaderWrapper>}
        {!loading &&
          rides?.length > 0 &&
          rides.map((ride, index) => (
            <RideCard key={index} onClick={() => handleSearchRide(ride)}>
              {renderRoute(ride)}
              <PriceTag>
                <small>RS.</small>
                <strong>{ride.ticket}</strong>
              </PriceTag>
            </RideCard>
          ))}
      </GridContainer>
    </SectionContainer>
  );
}

// Styled Components (Light Theme)

const SectionContainer = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  background: white; /* Ensure clean background */

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 2rem 1rem;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: #054752; /* Primary */
  font-weight: 700;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 1.6rem;
    line-height: 1.3;
  }

  @media (max-width: 480px) {
    font-size: 1.4rem;
  }
`;

const ViewAllLink = styled(Link)`
  color: #00796b;
  text-decoration: none;
  font-weight: 600;
  transition: opacity 0.2s;
  font-size: 1rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }

  @media (max-width: 350px) {
    grid-template-columns: 1fr;
  }
`;

const RideCard = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);

  &:hover {
    transform: translateY(-5px);
    border-color: #054752;
    box-shadow: 0 10px 20px rgba(5, 71, 82, 0.1);
  }
`;

const RouteInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const Location = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #37474f;
  font-size: 0.95rem;
  font-weight: 500;

  .icon {
    color: #00796b;
    font-size: 0.8rem;
  }
`;

const ArrowWrapper = styled.div`
  padding-left: 0.4rem;
  color: #90a4ae;
  font-size: 0.8rem;
`;

const PriceTag = styled.div`
  background: #f0fdfa;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  color: #054752;
  display: flex;
  align-items: baseline;
  gap: 2px;
  border: 1px solid #b2dfdb;

  small {
    font-size: 0.7rem;
    font-weight: 600;
  }

  strong {
    font-size: 1.2rem;
    font-weight: 700;
  }
`;

const LoaderWrapper = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  padding: 2rem;
`;
